import { useCallback, useEffect, useMemo, useState } from "react";
import { getLogsCartao } from "services/logsCartao";
import type {
  ILogCartaoEntry,
  ILogsCartaoFilters,
  ILogsCartaoResumo,
} from "interfaces/logsCartao";
import { DEFAULT_LOGS_CARTAO_FILTERS } from "interfaces/logsCartao";

const DEFAULT_PAGE_SIZE = 25;

const EMPTY_RESUMO: ILogsCartaoResumo = {
  total: 0,
  cartaoTentativas: 0,
  cartaoErros: 0,
  notificacoes: 0,
  boletos: 0,
  pixGerados: 0,
};

function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function matchesFilters(
  entry: ILogCartaoEntry,
  filters: ILogsCartaoFilters
): boolean {
  if (filters.categoria && entry.categoria !== filters.categoria) {
    return false;
  }
  if (filters.status && entry.status !== filters.status) {
    return false;
  }

  const numero = filters.numeroInscricao.trim().toLowerCase();
  if (numero && !(entry.numeroInscricao ?? "").toLowerCase().includes(numero)) {
    return false;
  }

  const nome = filters.nomeCliente.trim().toLowerCase();
  if (nome && !(entry.customer?.name ?? "").toLowerCase().includes(nome)) {
    return false;
  }

  const cpfDigits = onlyDigits(filters.cpf);
  if (cpfDigits && !onlyDigits(entry.customer?.cpf ?? "").includes(cpfDigits)) {
    return false;
  }

  return true;
}

export function useLogsCartao() {
  const [rows, setRows] = useState<ILogCartaoEntry[]>([]);
  const [raw, setRaw] = useState("");
  const [resumo, setResumo] = useState<ILogsCartaoResumo>(EMPTY_RESUMO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ILogsCartaoFilters>(
    DEFAULT_LOGS_CARTAO_FILTERS
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, status } = await getLogsCartao();
      if (status === 200) {
        setRows(data.data ?? []);
        setRaw(data.raw ?? "");
        setResumo(data.resumo ?? EMPTY_RESUMO);
      } else {
        setRows([]);
        setRaw("");
        setResumo(EMPTY_RESUMO);
        setError("Não foi possível carregar os logs de cobrança.");
      }
    } catch {
      setRows([]);
      setRaw("");
      setResumo(EMPTY_RESUMO);
      setError("Não foi possível carregar os logs de cobrança.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs, refreshTick]);

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, filters)),
    [rows, filters]
  );

  const paginatedRows = useMemo(() => {
    const start = page * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const isFiltered = useMemo(
    () =>
      Boolean(
        filters.numeroInscricao.trim() ||
          filters.nomeCliente.trim() ||
          filters.cpf.trim() ||
          filters.categoria ||
          filters.status
      ),
    [filters]
  );

  const isEmpty = !loading && filteredRows.length === 0;

  const refetch = useCallback(() => {
    setRefreshTick((tick) => tick + 1);
  }, []);

  const setFilter = useCallback(
    <K extends keyof ILogsCartaoFilters>(key: K, value: ILogsCartaoFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPage(0);
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_LOGS_CARTAO_FILTERS);
    setPage(0);
  }, []);

  const onPaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    []
  );

  return {
    rows: paginatedRows,
    allRows: rows,
    filteredRows,
    raw,
    resumo,
    loading,
    error,
    filters,
    setFilter,
    clearFilters,
    isFiltered,
    isEmpty,
    page,
    pageSize,
    total: filteredRows.length,
    refetch,
    onPaginationModelChange,
  };
}
